import React, { useState } from "react";
import {
  User,
  Wallet,
  Download,
  Lock,
  Eye,
  EyeOff,
  Trash2,
  Calendar,
  ChevronRight,
  X,
} from "lucide-react";
import { CURRENCY } from "../../constants";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/auth-slice";
import { formatAmount } from "../../utils";
import { useNavigate } from "react-router-dom";
import {
  useChangePasswordMutation,
  useDeleteAccountMutation,
} from "../../store/apis";
import { handleApiError } from "../../errors";
import { toastService } from "../notificationPage/ToastContainer";

const Account = () => {
  const [activeModal, setActiveModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [passwordFields, setPasswordFields] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [changePassword, { isLoading: isChangePassowrdLoading }] =
    useChangePasswordMutation();
  const [deleteAccount, { isLoading: isDeleteAccountLoading }] =
    useDeleteAccountMutation();

  const user = useSelector(selectUser);
  const navigate = useNavigate();

  const handlePasswordChange = (field, value) => {
    setPasswordFields((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear any messages when user types
    setPasswordError("");
    setPasswordSuccess("");
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const submitPasswordChange = async () => {
    // Validation
    if (
      !passwordFields.oldPassword ||
      !passwordFields.newPassword ||
      !passwordFields.confirmPassword
    ) {
      setPasswordError("All password fields are required");
      return;
    }

    if (passwordFields.newPassword !== passwordFields.confirmPassword) {
      setPasswordError("New passwords don't match");
      return;
    }

    if (passwordFields.newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }
    try {
      const response = await changePassword({
        customer_id: user?.id,
        oldPassword: passwordFields.oldPassword,
        newPassword: passwordFields.newPassword,
      }).unwrap();
      toastService.success(response.message);
      setPasswordSuccess("Password changed successfully");
      // Reset fields
      setPasswordFields({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      handleApiError(error);
    }
  };

  // Delete Account Modal Component
  const DeleteAccountModal = () => {
    const handleDeleteAccount = async () => {
      if (deleteConfirmation.toLowerCase() !== "delete") {
        setPasswordError('Please type "DELETE" to confirm account deletion');
        return;
      }
      try {
        const response = await deleteAccount({
          customer_id: user?.id,
        }).unwrap();
        toastService.success(response.message);
        setPasswordSuccess("Password changed successfully");
        alert("Account deleted successfully");
        // Redirect to login or home
        navigate("/");
      } catch (error) {
        handleApiError(error);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-teal rounded-2xl p-6 w-full max-w-md relative">
          <button
            onClick={() => setActiveModal(null)}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          >
            <X size={20} />
          </button>

          <h2 className="text-2xl font-bold mb-4 text-red-600">
            Delete Account
          </h2>

          <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-lg mb-5">
            <p className="text-red-700 dark:text-red-200">
              Warning: This action is irreversible. All your data will be
              permanently deleted.
            </p>
          </div>

          <div className="mb-5">
            <label className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">
              Type "DELETE" to confirm
            </label>
            <input
              type="text"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              className="w-full p-3 border rounded-lg dark:bg-teal/30 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder='Type "DELETE"'
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              onClick={() => setActiveModal(null)}
              className="order-2 sm:order-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white px-5 py-3 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteAccount}
              className="order-1 sm:order-2 bg-red-600 text-white px-5 py-3 rounded-lg hover:bg-red-700 transition font-medium"
            >
              {isDeleteAccountLoading ? "DELETE..." : "DELETE ACCOUNT"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-6 pb-20 bg-gray-50 dark:bg-teal/5">
      {/* Render delete account modal if active */}
      {activeModal === "delete" && <DeleteAccountModal />}

      <div className="max-w-3xl mx-auto px-4">
        {/* Profile Header */}
        <div className="bg-white dark:bg-teal shadow-lg rounded-2xl p-6 mb-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Avatar and User Info */}
            <div className="flex flex-col items-center md:items-start text-center md:text-left">
              <div className="w-24 h-24 bg-yellow/20 dark:bg-yellow/30 rounded-full flex items-center justify-center mb-4">
                <User className="w-12 h-12 text-yellow dark:text-white" />
              </div>

              <h2 className="text-2xl font-bold dark:text-white">
                {user.username}
              </h2>

              <p className="text-gray-600 dark:text-gray-300 mt-1">
                @{user.username}
              </p>
            </div>

            {/* Balance and Actions */}
            <div className="flex flex-col items-center md:items-end mt-4 md:mt-0 md:ml-auto">
              {/* <div className="bg-gray-100 dark:bg-teal/40 rounded-xl p-4 mb-4 w-full md:w-auto">
                <p className="text-gray-500 dark:text-gray-300 text-sm mb-1">
                  Balance
                </p>
                <p className="text-2xl font-bold dark:text-yellow">
                  {formatAmount(user.balance, "ETB", "en-ET")}
                </p>
              </div> */}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <button
                  onClick={() => navigate("/funds/deposit")}
                  className="flex items-center justify-center bg-yellow hover:bg-yellow/90 dark:bg-yellow dark:hover:bg-yellow/90 rounded-lg px-5 py-3 text-teal text-base font-medium transition w-full sm:w-auto"
                >
                  <Wallet size={20} className="mr-2" />
                  Deposit
                </button>

                <button
                  onClick={() => navigate("/funds/withdraw")}
                  className="flex items-center justify-center bg-teal text-yellow border-2 border-teal dark:border-white hover:bg-transparent dark:hover:bg-transparent dark:hover:text-white hover:text-teal transition-all duration-300 dark:bg-white rounded-lg dark:text-teal px-5 py-3 w-full sm:w-auto font-medium"
                >
                  <Download size={20} className="mr-2" />
                  Withdraw
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Password Change Section */}
        <div className="bg-white dark:bg-teal rounded-2xl p-6 mb-6 shadow-md">
          <div className="flex items-center gap-3 mb-5">
            <Lock className="text-teal dark:text-yellow" size={24} />
            <h3 className="text-xl font-semibold dark:text-white">
              Change Password
            </h3>
          </div>

          {passwordError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
              {passwordError}
            </div>
          )}

          {passwordSuccess && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
              {passwordSuccess}
            </div>
          )}

          <div className="space-y-4">
            {/* Old Password */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showPassword.old ? "text" : "password"}
                  value={passwordFields.oldPassword}
                  onChange={(e) =>
                    handlePasswordChange("oldPassword", e.target.value)
                  }
                  className="w-full p-3 pr-10 border rounded-lg dark:bg-teal/30 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-teal focus:border-teal"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("old")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400"
                >
                  {showPassword.old ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword.new ? "text" : "password"}
                  value={passwordFields.newPassword}
                  onChange={(e) =>
                    handlePasswordChange("newPassword", e.target.value)
                  }
                  className="w-full p-3 pr-10 border rounded-lg dark:bg-teal/30 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-teal focus:border-teal"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("new")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400"
                >
                  {showPassword.new ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword.confirm ? "text" : "password"}
                  value={passwordFields.confirmPassword}
                  onChange={(e) =>
                    handlePasswordChange("confirmPassword", e.target.value)
                  }
                  className="w-full p-3 pr-10 border rounded-lg dark:bg-teal/30 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-teal focus:border-teal"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("confirm")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400"
                >
                  {showPassword.confirm ? (
                    <EyeOff size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </button>
              </div>
            </div>

            <button
              onClick={submitPasswordChange}
              className="mt-2 bg-yellow hover:bg-buttonGradient text-white py-3 px-5 rounded-lg font-medium transition w-full sm:w-auto"
            >
              {isChangePassowrdLoading ? "UPDATING..." : "UPDATE PASSWORD"}
            </button>
          </div>
        </div>

        {/* Delete Account Section */}
        <div className="bg-white dark:bg-teal rounded-2xl p-6 shadow-md">
          <div className="border-b dark:border-gray-700 pb-4 mb-4">
            <h3 className="text-xl font-semibold dark:text-white">
              Account Settings
            </h3>
          </div>

          <div
            onClick={() => setActiveModal("delete")}
            className="flex items-center justify-between p-4 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg cursor-pointer group transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 group-hover:bg-red-200 dark:group-hover:bg-red-900/30 transition-colors">
                <Trash2 size={20} />
              </div>
              <div>
                <h4 className="font-medium text-red-600 dark:text-red-400">
                  Delete Account
                </h4>
                <p className="text-sm text-red-500/70 dark:text-red-300/70">
                  Permanently remove your account
                </p>
              </div>
            </div>
            <ChevronRight className="text-red-400" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
